/**
 * TES GraphQL Client
 *
 * GraphQL client for communicating with TES via the backend proxy.
 * Requests go through /api/tes which adds the API key server-side.
 */

export interface GraphQLResponse<T = unknown> {
  data?: T;
  errors?: Array<{
    message: string;
    locations?: Array<{ line: number; column: number }>;
    path?: string[];
  }>;
}

export interface MutationResult {
  success: boolean;
  message: string;
  eventId?: string;
  queued?: boolean;
}

/**
 * Execute a GraphQL query or mutation against TES via the proxy
 */
export async function graphql<T = unknown>(
  query: string,
  variables: Record<string, unknown> = {}
): Promise<T> {
  const response = await fetch('/api/tes', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ query, variables }),
  });

  const result: GraphQLResponse<T> = await response.json();

  if (result.errors && result.errors.length > 0) {
    console.error('TES GraphQL errors:', result.errors);
    throw new Error(result.errors[0]?.message || 'GraphQL error');
  }

  if (!result.data) {
    throw new Error('No data returned from TES');
  }

  return result.data;
}

// =============================================================================
// MUTATIONS
// =============================================================================

const CREATE_THING = `
  mutation CreateThing($input: ThingInput!) {
    createThing(input: $input) {
      success
      message
      eventId
      queued
    }
  }
`;

const ADD_THING_STATUS = `
  mutation AddThingStatus($id: String!, $input: ThingStatusInput!) {
    addThingStatus(id: $id, input: $input) {
      success
      message
      eventId
      queued
    }
  }
`;

// =============================================================================
// QUERIES
// =============================================================================

const GET_THING = `
  query GetThing($id: String!) {
    thing(id: $id) {
      id
      name
      product_ref
      current_stage
      current_status
      quantity
      holder_id
      holder_type
      status_history {
        parent_status
        status
        timestamp
        data
      }
      data
      created_at
      updated_at
    }
  }
`;

// =============================================================================
// LIFECYCLE STAGES (for kiosk events)
// =============================================================================

export type LifecycleStage =
  | 'manufactured'
  | 'sourced'
  | 'in_stock'
  | 'in_transit'
  | 'delivered'
  | 'sold'
  | 'in_use'
  | 'captured'
  | 'identified'
  | 'valued'
  | 'returned'
  | 'received'
  | 'processing'
  | 'inspecting'
  | 'refurbishing'
  | 'repairing'
  | 'processed'
  | 'certified'
  | 'listed'
  | 'resold'
  | 'recycled'
  | 'donated'
  | 'disposed'
  | 'issue'
  | 'rejected'
  | 'lost';

// =============================================================================
// THING INPUT TYPES
// =============================================================================

export interface ThingStatusInput {
  parent_status: LifecycleStage;
  status: string;
  timestamp?: number; // Unix timestamp in seconds
  location_id?: string;
  holder_id?: string;
  holder_type?: 'customer' | 'warehouse' | 'store' | 'carrier' | 'processor' | 'manufacturer' | 'unknown';
  value?: number;
  data?: Record<string, unknown>;
}

export interface ThingInput {
  id?: string;
  name?: string;
  product_ref?: string;
  quantity?: number;
  unit?: string;
  condition?: string;
  holder_id?: string;
  holder_type?: string;
  location_id?: string;
  data?: Record<string, unknown>;
  statuses?: ThingStatusInput[];
  holder?: {
    id?: string;
    type?: string;
    name?: string;
    email?: string;
    data?: Record<string, unknown>;
  };
}

export interface Thing {
  id: string;
  name?: string;
  product_ref?: string;
  current_stage?: LifecycleStage;
  current_status?: string;
  quantity?: number;
  holder_id?: string;
  holder_type?: string;
  status_history?: Array<{
    parent_status: LifecycleStage;
    status: string;
    timestamp: number;
    data?: Record<string, unknown>;
  }>;
  data?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

// =============================================================================
// CLIENT METHODS
// =============================================================================

/**
 * Create a new thing in TES
 */
export async function createThing(input: ThingInput): Promise<MutationResult> {
  const data = await graphql<{ createThing: MutationResult }>(CREATE_THING, { input });
  return data.createThing;
}

/**
 * Add a status to a thing's lifecycle history
 */
export async function addThingStatus(
  id: string,
  input: ThingStatusInput
): Promise<MutationResult> {
  const data = await graphql<{ addThingStatus: MutationResult }>(ADD_THING_STATUS, {
    id,
    input,
  });
  return data.addThingStatus;
}

/**
 * Get a thing by ID
 */
export async function getThing(id: string): Promise<Thing | null> {
  const data = await graphql<{ thing: Thing | null }>(GET_THING, { id });
  return data.thing;
}

// =============================================================================
// HOLDER OPERATIONS (for gamification sync)
// =============================================================================

const GET_HOLDER = `
  query GetHolder($id: String!) {
    holder(id: $id) {
      id
      name
      email
      type
      data
      created_at
      updated_at
    }
  }
`;

const UPDATE_HOLDER = `
  mutation UpdateHolder($id: String!, $input: HolderInput!) {
    updateHolder(id: $id, input: $input) {
      success
      message
      holder_id
    }
  }
`;

const CREATE_HOLDER = `
  mutation CreateHolder($input: HolderInput!) {
    createHolder(input: $input) {
      success
      message
      holder_id
    }
  }
`;

export interface Holder {
  id: string;
  name?: string;
  email?: string;
  type?: string;
  data?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export interface HolderInput {
  id?: string;
  name?: string;
  email?: string;
  type?: 'customer' | 'warehouse' | 'store' | 'carrier' | 'processor' | 'manufacturer' | 'unknown';
  data?: Record<string, unknown>;
}

export interface HolderMutationResult {
  success: boolean;
  message: string;
  holder_id?: string;
}

/**
 * Get a holder by ID
 */
export async function getHolder(id: string): Promise<Holder | null> {
  const data = await graphql<{ holder: Holder | null }>(GET_HOLDER, { id });
  return data.holder;
}

/**
 * Update a holder's attributes
 */
export async function updateHolder(id: string, input: HolderInput): Promise<HolderMutationResult> {
  const data = await graphql<{ updateHolder: HolderMutationResult }>(UPDATE_HOLDER, { id, input });
  return data.updateHolder;
}

/**
 * Create a new holder
 */
export async function createHolder(input: HolderInput): Promise<HolderMutationResult> {
  const data = await graphql<{ createHolder: HolderMutationResult }>(CREATE_HOLDER, { input });
  return data.createHolder;
}
